"use client"

import styles from "./AutologousMicrograftPage.module.css"
import Header from "../../components/Header/Header"
import TreatmentTemplate from "../../components/TreatmentTemplate/TreatmentTemplate"
import ProcessSteps from "../../components/ProcessSteps/ProcessSteps"
import InfoSection from "../../components/InfoSection/InfoSection"
import FAQ from "../../components/FAQ/FAQ"
import Reviews from "../../components/Reviews/Reviews"
import Benefits from "../../components/Benefits/Benefits"
import Appointment from "../../components/Appointment/Appointment"
import beforeImg from "../../assets/images/Autologous-1.jpg"
import afterImg from "../../assets/images/Autologous-2.webp"
import processBeforeImg from "../../assets/images/depositphotos_2.jpg"
import processDuringImg from "../../assets/images/depositphotos.jpg"
import processAfterImg from "../../assets/images/Autologous-after-2.jpg"
import syringeImg from "../../assets/images/syringe-2.jpg"
import appointmentImg from "../../assets/images/autologousmicrograft.jpg"
import Footer from "../../components/Footer/Footer"

export default function AutologousMicrograftPage() {
  return (
    <>
      <Header />
      <TreatmentTemplate
        title="Autologous Micrograft"
        beforeImg={beforeImg}
        afterImg={afterImg}
        description={
          "Autologous Micrograft Treatment represents a groundbreaking approach to combating hair loss by harnessing the body's innate regenerative potential. This advanced technique focuses on relocating a patient's own robust hair follicles—sourced from the donor zone at the back of the scalp—to areas experiencing thinning or loss. As the procedure utilizes the patient's natural tissue, it virtually eliminates the possibility of adverse reactions, positioning it as an exceptionally safe and reliable path to hair restoration. The transplanted follicles adapt to their new environment and maintain their normal growth cycle, delivering enduring, natural-looking results that seamlessly integrate with existing hair."
        }
        facts={[
          { icon: "⏱️", title: "Procedure Timeline", sub: "30 – 60 Minutes" },
          { icon: "💚", title: "Healing & Aftercare", sub: "1 Day" },
          { icon: "✈️", title: "Recommended Stay in Istanbul", sub: "1 Day" },
          { icon: "🛏️", title: "Your Stay", sub: "1 Day" },
          { icon: "👨‍⚕️", title: "Ongoing Medical Support", sub: "1 Year" },
          { icon: "🚐", title: "Travel Arrangements", sub: "VIP Transfer" },
        ]}
      />
      <ProcessSteps 
        title="Your Step-by-Step Journey to Autologous Micrograft"
        steps={[
          {
            id: "before",
            label: "Before",
            content: (
              <ul>
                <li>The first step is an in-depth scalp and hair follicle analysis performed by our expert team. This assessment will form the foundation of your personalized treatment plan.</li>
                <li>To maximize the effectiveness of your treatment, we recommend avoiding alcohol and smoking for 48 hours prior to your appointment.</li>
                <li>If you are taking any blood-thinning or skin-related medications (prescription or over-the-counter), please consult with your physician in advance regarding their use before the procedure.</li>
                <li>Simply wash your hair with a mild, residue-free shampoo on the morning of your treatment. No other special preparation is required.</li>
              </ul>
            ),
            image: processBeforeImg
          },
          {
            id: "during",
            label: "During",
            content: (
              <ul>
                <li>The treatment begins with the application of local anesthesia to the affected areas to guarantee you remain completely comfortable and pain-free throughout.</li>
                <li>Using advanced micrografting technology, our specialists will gently and meticulously harvest healthy, robust hair follicles from your donor site.</li>
                <li>The carefully prepared follicles are then artistically transplanted into the thinning areas with meticulous attention to angle, density, and natural hairline design.</li>
                <li>The session is typically completed within 30 to 60 minutes. The exact time is tailored to the individual scope of your treatment plan for optimal results.</li>
              </ul>
            ),
            image: processDuringImg
          },
          {
            id: "after",
            label: "After",
            content: (
              <ul>
                <li>It is common to experience mild redness or tenderness in the treated area. This is a normal sign of healing and usually subsides within a day or two.</li>
                <li>Please refrain from washing your hair for the first 24 hours. After this period, you may resume washing gently using only a mild, non-irritating shampoo for the next several days.</li>
                <li>The treatment requires no downtime. You are free to return to your regular routine, including work and most daily activities, immediately after your procedure.</li>
                <li>The transformation is gradual. You can expect to see initial results emerging within 1 to 3 months, with visible improvements in hair density and strength continuing to develop over the following 6 to 12 months.</li>
              </ul>
            ),
            image: processAfterImg
          }
        ]}
      />
      <InfoSection 
        image={syringeImg}
        sections={[
          {
            heading: "Understanding Autologous Micrograft Treatment",
            content: [
              "Autologous Micrograft Treatment is an advanced, minimally invasive solution for hair loss that harnesses your body's own natural resources. This sophisticated approach involves transplanting healthy hair follicles from a donor site to areas of thinning, promoting natural hair regrowth from within.",
              "Because the procedure uses your own tissue (autologous grafts), it completely avoids the risk of rejection or allergic reaction, offering a safe and biocompatible treatment. The transplanted follicles gradually establish themselves in their new location, leading to permanent, natural-looking hair restoration that blends seamlessly with your existing hair. Renowned for its precision and minimal downtime, this technique provides a compelling alternative to more invasive surgical options."
            ]
          },
          {
            heading: "The Step-by-Step Treatment Journey",
            content: [
              "The Autologous Micrograft Treatment process involves several carefully orchestrated steps:",
              null, // This will be replaced with a list
              "This comprehensive approach ensures minimal recovery time while maximizing natural, long-lasting results that blend seamlessly with your existing hair."
            ],
            list: [
              "Personalized Consultation & Analysis: Your journey starts with a thorough evaluation of your scalp and hair loss. We identify the most suitable donor area, typically the back or sides of the head, where hair follicles are most resilient.",
              "Gentle Follicle Harvesting: Using refined Follicular Unit Extraction (FUE) methods, individual healthy follicles are gently and precisely removed from the donor area, ensuring minimal disruption to the scalp.",
              "Artistic Graft Preparation: The harvested follicles are then meticulously prepared under microscopic guidance. They are processed into micrografts—tiny, naturally occurring follicular units—ready for transplantation.",
              "Precision Placement: These micrografts are strategically implanted into the recipient sites. Our specialists artfully replicate your natural hair growth direction and density for an undetectable, full result.",
              "The Recovery & Growth Phase: Post-treatment, you may experience mild and temporary redness. Healing is generally swift, allowing a rapid return to daily life. The exciting phase of new hair growth typically becomes visible within several months, with density and thickness continuing to improve for up to a year."
            ]
          },
          {
            heading: "Synergistic Benefits: Combining Micrograft Therapy with Hair Transplantation",
            content: [
              "For comprehensive results, Autologous Micrograft Treatment can be powerfully combined with a traditional hair transplant. In this synergistic approach, the micrograft therapy does more than just add new follicles; it actively nourishes and strengthens both the newly transplanted and existing hair. This dual action enhances overall follicular survival, improves scalp health, and ensures a denser, more robust final outcome."
            ]
          }
        ]}
      />
      <FAQ
        leftSection={{
          title: "A Superior Path to Hair Restoration",
          description: "Autologous Micrograft Treatment stands out for its significant benefits compared to conventional hair restoration techniques. A key advantage is the minimal recovery period; patients can typically return to their everyday routines almost immediately. Leveraging state-of-the-art technology and a fully personalized strategy, this treatment has emerged as the preferred choice for individuals who demand a safe, effective, and authentically natural solution for hair rejuvenation.",
          image: beforeImg
        }}
        faqs={[
          {
            question: "What conditions can Autologous Micrograft Treatment address?",
            answer: "This treatment is designed to address hair thinning and loss by strategically relocating your own resilient hair follicles to areas where density has decreased. This method guarantees that the results are not only natural in appearance but also permanent, as the transplanted hair is genetically resistant to balding."
          },
          {
            question: "Can this treatment be enhanced with a hair transplant?",
            answer: "Absolutely. When used in synergy with a hair transplant, micrograft therapy creates an optimal environment for growth. It enhances the survival rate of the newly placed grafts while simultaneously nourishing and revitalizing the surrounding existing hair. This dual-action approach is key to achieving superior density and more durable outcomes."
          },
          {
            question: "What level of discomfort should I expect?",
            answer: "Patient comfort is a cornerstone of the procedure. It is minimally invasive and conducted under local anesthesia, so you should feel no pain during the treatment. Any post-procedure sensitivity is generally minimal and can be easily managed with over-the-counter pain relief if needed."
          },
          {
            question: "What is the typical duration of a treatment session?",
            answer: "The duration of your session is tailored to the extent of the area being treated, generally ranging from 4 to 8 hours. For optimal results and patient comfort, more extensive cases may be planned over two or more separate sessions."
          },
          {
            question: "How soon can I expect to see visible improvements?",
            answer: "The journey to fuller hair is a gradual process. Initial signs of progress, such as improved hair strength and thickness, often become apparent within 3 to 6 months. The most significant and complete visual results will continue to mature, typically reaching their full potential between 9 and 12 months post-treatment."
          }
        ]}
      />
      <Reviews />
      <Benefits 
        mainTitle="Advantages of the Micrograft Approach"
        sections={[
          {
            
            subtitle: "The Autologous Micrograft Treatment provides a multitude of significant benefits for those seeking hair restoration:",
            benefits: [
              {
                title: "Authentically Natural Outcome:",
                description: "By utilizing your own hair follicles, the regrowth is indistinguishable from your existing hair, in terms of texture, growth pattern, and appearance."
              },
              {
                title: "Imperceptible Healing:",
                description: "The sophisticated techniques used for follicle extraction result in pinpoint incisions that heal quickly and are virtually undetectable."
              },
              {
                title: "A Lasting Investment:",
                description: "The follicles transplanted from the donor area are genetically programmed to be permanent, offering a durable solution to hair loss."
              },
              {
                title: "Unparalleled Biocompatibility:",
                description: "Since the grafts originate from your own body, there is absolutely no risk of allergic reaction or tissue rejection."
              },
              {
                title: "Swift Return to Routine:",
                description: "The procedure is designed for minimal interruption to your life, with most individuals resuming their daily activities within a very short timeframe."
              }
            ],
            conclusion: "In conjunction with a traditional hair transplant, this therapy fortifies both the newly implanted and native hair follicles, leading to a denser, more resilient head of hair."
          },
          {
            title: "The Case for Choosing Autologous Micrografts",
            paragraphs: [
              "Opting for Autologous Micrograft Treatment means selecting a safe, reliable, and natural-pathway to combat hair loss. It is the preferred option for those who desire a permanent solution through a minimally invasive procedure, free from the risks of synthetic materials or complex surgery. Its role as a complement to hair transplantation extends its value, simultaneously reinforcing new grafts and rejuvenating the existing hair landscape.",
              "Patients benefit from increased hair density, restored hairlines that look entirely natural, a brief recovery period, and enduring outcomes, cementing its status as a forefront technology in hair restoration."
            ]
          },
          {
            title: "Harnessing the Body's Innate Regenerative Power",
            paragraphs: [
              "This innovative technology works by leveraging your body's inherent ability to heal and regenerate. The process of extracting and redistributing micrografts effectively stimulates the scalp, encouraging a healthy environment for hair to thrive. These relocated follicles are designed to grow consistently and robustly, providing sustainable, long-term results.",
              "As a minimally invasive yet powerfully effective modality, this technology empowers patients to effectively address hair thinning, regain volume, and restore their confidence. It stands as a safe and tailored strategy, whether as a standalone treatment or an enhancement to transplantation, for achieving a thicker, healthier head of hair."
            ]
          },
          {
            title: "Identifying the Ideal Candidate",
            subtitle: "This treatment is particularly well-suited for:",
            benefits: [
              "Men and women in the initial or intermediate stages of hair thinning.",
              "Those pursuing a natural, effective alternative to major surgical interventions.",
              "Individuals aiming to enhance the thickness and vitality of their current hair.",
              "Candidates for hair transplantation who wish to maximize their results through a combined methodology.",
              "Anyone seeking a permanent solution with a rapid recovery and a natural-looking end result."
            ],
            conclusion: "If your goal is a comprehensive, advanced approach to hair restoration, Autologous Micrograft Treatment—on its own or integrated with a transplant—may be the optimal choice for you."
          }
        ]}
      />
      <Appointment 
        title="Begin Your Hair Restoration Journey"
        paragraphs={[
          "Embark on the path to reclaiming a fuller head of hair and renewed self-assurance. Our advanced Autologous Micrograft Technology is designed to deliver personalized results for thicker, healthier hair. From your initial inquiry to your final result, our dedicated specialists will be with you, ensuring a seamless and supportive experience.",
          "Schedule your personal consultation today. Discover your personalized path to results and see how Autologous Micrograft Treatment can revitalize your appearance and confidence."
        ]}
        image={appointmentImg}
        imageAlt="Autologous Micrograft Treatment Consultation"
      />
      <Footer />
    </>
  )
}


